
/* eslint-disable padding-line-between-statements */
import React, { useCallback, useContext, useState, useMemo } from 'react';
import { BreakpointTrackerContext } from '@jutro/layout';
import _ from 'lodash';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { Loader, useModal, InlineNotification } from '@jutro/components';
import CustomLoader from 'producer-engage/src/components/CustomLoader/CustomLoader';
import { useTranslator } from '@jutro/locale';
import wizardStyles from 'twia-portals-wizard-components-ui/common/TWIAWizard.module.scss';
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil';
import TWIAAdditionalInsuredComponent from 'twia-capability-quoteandbind-cp-react/components/TWIAAdditionalInformation/TWIAAdditionalInsuredComponent/TWIAAdditionalInsuredComponent.jsx';
import PremiumFinanceComponent from 'twia-capability-quoteandbind-cp-react/components/TWIAAdditionalInformation/PremiumFinanceComponent/PremiumFinanceComponent.jsx';
import { ENTDdocumentUploadComponent } from 'gw-capability-gateway-react';

import { GatewayEndorsementService as LoadSaveService} from 'twia-capability-gateway-policychange';
import { CustomQuoteService } from 'twia-capability-quoteandbind-residential';
import PrintDocumentUtils from 'ent-capability-quoteandbind-common-react/util/PrintDocumentUtils.js';
import PrintDocumentConfirmationPopup from 'ent-capability-quoteandbind-common-react/pages/PrintDocument/PrintDocumentConfirmationPopup';
import PrintDocumentErrorPopup from 'ent-capability-quoteandbind-common-react/pages/PrintDocument/PrintDocumentErrorPopup';


import { resetElectronicPaymentData } from 'ent-capability-quoteandbind-common-react/util/ResetElectronicData';
import styles from './AdditionalInformationPage.module.scss';

import metadata from './AdditionalInformationPage.metadata.json5';


function AdditionalInformationPage(props) {
    const { id, wizardData: submissionVM, updateWizardData, onResolve, onAfterClose } = props;
    const breakpoint = useContext(BreakpointTrackerContext);
    const { onValidate } = useValidation(id);
    const [showErrors, setShowErrors] = useState(false);
    const { authHeader } = useAuthentication();
    const translator = useTranslator();
    const [isLoading, setIsLoading] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const { showAlert, showConfirm, showModal } = useModal();
    const [invalidComponent, setInvalidComponents] = useState([]);
    const [validationMessages, setValidationMessages] = useState([]);
    const jobNumber = _.get(submissionVM, 'jobID.value');
    const jobType = _.get(submissionVM, 'baseData.jobType.value.code');
    const productCode = _.get(submissionVM, 'baseData.productName.value');
    const uwCompany = 'twia';
    const locations = _.get(submissionVM, 'lobData.commercialProperty.coverables.locations.value');

    const findInvalidComponent = useCallback((isValid, componentID) => {
        setShowErrors(false);

        if (isValid === false) {
            setInvalidComponents((oldValue) => [...oldValue, componentID]);
        }

        if (isValid === true) {
            setInvalidComponents((oldValue) => oldValue.filter((val) => val !== componentID));
        }
    }, []);
    
    // const quotePrintDocument = useCallback(async () => {
    //     setIsPrinting(true);
    //     await PrintDocumentUtil.printDocument(submissionVM, showConfirm, showAlert, translator, authHeader, setIsPrinting);
    // }, [authHeader, showAlert, showConfirm, submissionVM, translator])

    const quotePrintDocument = useCallback(() => {
        const {getPublicID, getPath, getQuoteStatus} = PrintDocumentUtils(submissionVM);
        setIsPrinting(true);
        
        const publicID = getPublicID();
        const docPath = getPath();
        const quoteStatus = getQuoteStatus();       
        const componentProps = { submissionVM, setIsPrinting, isOpen: true, onResolve, onAfterClose, docPath, quoteStatus};       
        if (quoteStatus === 'Quoted' || quoteStatus === 'Submitted_Ext' || quoteStatus === 'Renewing') {
            CustomQuoteService.generateDocument_TWIA(publicID, 'quoteSummary', authHeader).then(() => {
                showModal(<PrintDocumentConfirmationPopup {...componentProps} />);
            });
        }  else {
            showModal(<PrintDocumentErrorPopup {...componentProps} />);
        }
    }, [authHeader, onAfterClose, onResolve, showModal, submissionVM])

    const validationContent = useCallback(
        item => (
                <InlineNotification id={item} 
                    message={item}
                    type= "error"
                    className= {wizardStyles.marginBottom}
                    isDismissable= {false}
                />
            ),
        []
    );

    const generateOverrides = useMemo(() => {
        const overrides = validationMessages.map((item, index) => ({
                [`errorMsg${index}`]: {
                    content: validationContent(item),
                },
            }));
        return Object.assign({}, ...overrides);
    }, [validationMessages, validationContent]);

    const onNext = useCallback(async () => {
        if (invalidComponent.length > 0) {
            setShowErrors(true);
            return false
        }
        setShowErrors(false);
        try {
            submissionVM.validationParams_TWIA.value = {
                validationLevel: 'default',
                validationWizardStep: 'AdditionalInfo',
                skipAllValidation: false
            };
            resetElectronicPaymentData(submissionVM, updateWizardData)
            submissionVM.value = await LoadSaveService.saveQuotedEndorsement_TWIA(
                submissionVM.value,
                authHeader
            );
            const validationIssues = _.get(submissionVM, 'errorsAndWarnings.value.validationIssues.issues');
            if (validationIssues && validationIssues.length > 0) {
                setShowErrors(true);
                setValidationMessages(_.uniq(validationIssues.map(vi => vi.reason)));
                return false;
            } 
            return submissionVM;
            
        } catch (error) {
            setShowErrors(false);
            ErrorPopupUtil.showSaveErrorPopup(showAlert, true)
            return false;
        }
       // return submissionVM;
    }, [invalidComponent, submissionVM, updateWizardData, authHeader, showAlert])

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        additionalNamedInsuredsDetails: {
            submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate
        },
        additionalNamedInsuredsAccordion: {
            errorState: _.includes(invalidComponent, 'TWIAAdditionalInsuredDetails') && showErrors
        },
        premiumFinanceDetails: {
            submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate
        },
        premiumFinanceAccordion: {
            errorState: _.includes(invalidComponent, 'PremiumFinanceDetails') && showErrors,
            visible:  false // US30334 -  TWIA - Remove Premium Finance Option - PE
        },
        // 2025-05-22 - Debra Frum - DE19212 - TWIA Underwriting Response activities are triggering under open transactions
        //   Functionality for TWIA didn't get implemented when TFPA & TWIA portals merged during move to Guidewire Cloud
        // 2022-05-24 - Debra Frum - US22167 - PC / PE Underwriting Response Activity
        //   Don't create a Review activity when a document is uploaded within a TWIA transaction workflow
        uploadDocumentsDetails: {
            jobNumber, jobType, productCode, uwCompany, locations, skipReviewActivity: true
        },
        validationMessages: {
            data: validationMessages
        },
        quotePagePrint: {
            visible : !isPrinting
        },
        cLoader: {
            visible: isPrinting
        },
        ...generateOverrides
    }
    const resolvers = {
        resolveCallbackMap: {
            quotePrintDocument
        },
        resolveClassNameMap: styles,
        resolveComponentMap: {
            AdditionalInsuredsComponent: TWIAAdditionalInsuredComponent,
            addlPremiumFinanceComponent: PremiumFinanceComponent,
            documentUploadComponent: ENTDdocumentUploadComponent,
            customLoader: CustomLoader
        },
    }

    return (
        <WizardPage
            onNext={onNext}
        >
             <Loader loaded={!isLoading}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showErrors}
            />
            </Loader>
        </WizardPage>
    )
}

export default AdditionalInformationPage
